import { useQuery, UseQueryResult } from "react-query";

import { makeRequest } from "@/util";

export type AccountStatus = {
  /**
   * False when the signed-in Firebase user has no Freshpaint user row
   */
  has_account: boolean;
  /**
   * Set when an invite is waiting for this email address. Accepting it is what
   * creates the user's row, so this is the escape hatch out of the no-account
   * state.
   */
  pending_invite_id: string | null;
};

/**
 * Polls the caller's account status. `get_account_status` is dispatched before
 * account resolution (see PreAccountActions in the backend), so it is one of
 * the few requests an accountless user can make successfully.
 *
 * We refetch on an interval and on window focus so that a user sitting on the
 * no-account splash screen is moved along as soon as someone invites them,
 * without needing to know to reload the page.
 */
export default function useAccountStatus(
  enabled = true,
): UseQueryResult<AccountStatus> {
  return useQuery<AccountStatus>(
    ["get_account_status"],
    async () => {
      const response = await makeRequest<AccountStatus>(
        "get_account_status",
        {},
      );
      return response.data;
    },
    {
      enabled,
      refetchInterval: 30_000,
      refetchOnWindowFocus: true,
      // An accountless user has nothing cached worth preserving, and a stale
      // "no account" answer is exactly what we're trying to avoid showing.
      staleTime: 0,
    },
  );
}
